import { useState } from "react";
import { useLocation } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquarePlus, Star, Bug, Lightbulb, Heart, MoreHorizontal, Loader2 } from "lucide-react";

const CATEGORIES = [
  { key: "bug", label: "Bug", Icon: Bug, color: "#EF4444" },
  { key: "idea", label: "Idea", Icon: Lightbulb, color: "#0044FF" },
  { key: "praise", label: "Praise", Icon: Heart, color: "#10B981" },
  { key: "other", label: "Other", Icon: MoreHorizontal, color: "#7C3AED" },
];

function pageLabel(pathname) {
  if (pathname === "/" || pathname.startsWith("/welcome")) return "Welcome";
  if (pathname.startsWith("/login")) return "Login";
  if (pathname.startsWith("/register")) return "Register";
  if (pathname.startsWith("/dashboard")) return "Projects Dashboard";
  if (pathname.startsWith("/portfolio")) return "Portfolio";
  if (pathname.startsWith("/status-report")) return "Status Report";
  if (pathname.startsWith("/build")) return "Build";
  if (pathname.startsWith("/library")) return "Library";
  if (pathname.startsWith("/admin")) return "Admin";
  if (pathname.startsWith("/projects/")) return "Project Detail";
  if (pathname.startsWith("/shared/report/")) return "Public Report";
  if (pathname.startsWith("/accept-invite/")) return "Accept Invite";
  if (pathname.startsWith("/go/")) return "Redirect";
  return pathname;
}

export default function FeedbackWidget() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("idea");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const label = pageLabel(loc.pathname);
  const fullPage = `${loc.pathname}${loc.search || ""}`;

  const reset = () => { setCategory("idea"); setRating(0); setHover(0); setMessage(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim() && !rating) {
      toast.error("Add a rating or a comment first.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/feedback", {
        page: fullPage,
        page_label: label,
        category,
        rating: rating || null,
        message: message.trim(),
      });
      toast.success("Thanks! Your feedback was sent.");
      setOpen(false);
      reset();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Could not send feedback");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="feedback-fab"
        title={`Share feedback about: ${label}`}
        className="fixed bottom-6 left-6 z-50 flex items-center gap-2 rounded-full bg-foreground text-background pl-3 pr-4 py-2.5 shadow-lg hover:scale-105 active:scale-95 transition-transform"
      >
        <MessageSquarePlus className="h-4 w-4" />
        <span className="text-sm font-semibold">Feedback</span>
      </button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-md" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Share feedback</DialogTitle>
            <DialogDescription>
              About <span className="font-semibold text-foreground" data-testid="feedback-page-label">{label}</span>
              <span className="text-muted-foreground"> · {fullPage}</span>
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <p className="text-sm font-semibold mb-2">Type</p>
              <div className="grid grid-cols-4 gap-2" data-testid="feedback-category">
                {CATEGORIES.map(({ key, label: cl, Icon, color }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setCategory(key)}
                    data-testid={`feedback-category-${key}`}
                    className={`flex flex-col items-center gap-1 rounded-lg border-2 py-2.5 transition-all ${category === key ? "border-current bg-secondary/50" : "border-border hover:border-foreground/30"}`}
                    style={category === key ? { color } : {}}
                  >
                    <Icon className="h-5 w-5" style={{ color }} />
                    <span className="text-xs font-semibold text-foreground">{cl}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold mb-2">Rating <span className="text-xs font-normal text-muted-foreground">(optional)</span></p>
              <div className="flex items-center gap-1.5" data-testid="feedback-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n === rating ? 0 : n)}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    data-testid={`feedback-star-${n}`}
                    className="p-0.5"
                  >
                    <Star
                      className="h-7 w-7 transition-colors"
                      style={{
                        color: (hover || rating) >= n ? "#F59E0B" : "#cbd5e1",
                        fill: (hover || rating) >= n ? "#F59E0B" : "transparent",
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold mb-2">Comment</p>
              <Textarea
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={`What's working or what could be better on the ${label} page?`}
                data-testid="feedback-message-input"
              />
            </div>

            <DialogFooter>
              <Button type="submit" disabled={saving} className="gap-2" data-testid="feedback-submit-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquarePlus className="h-4 w-4" />} Send feedback
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
